import { ExtendedDataProvider } from "@src/types";
import { useDataProvider, useNotify, useQueryWithStore } from "react-admin";
import React, { useEffect, useMemo, useState } from "react";
import JsonPanel from "@src/components/Common/JsonPanel";
import { Select, Space, Tooltip } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import { getTagForStates } from "@src/components/Common/Tags";
import { spinnerElement } from "../../Common/utils";

const Declarations = (props: any) => {
    const [options, setOptions] = useState<any[]>([]);
    // no version until we know one: the picker shows its placeholder rather than
    // a number no declaration has
    const [version, setVersion] = useState<any>(undefined);
    const [schemaVersion, setSchemaVersion] = useState<any>(0);
    const [config, setConfig] = useState<any>({});
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const notify = useNotify();

    const { service_item, resource } = props;

    const { data: versionsData } = useQueryWithStore({
        type: 'getAction',
        resource: resource,
        payload: { id: service_item.id, verb: 'declarations/versions' }
    });

    const { data: latestDeclarationData, loading: latestLoading } = useQueryWithStore({
        type: 'getAction',
        resource: resource,
        payload: { id: service_item.id, verb: 'declarations/latest' }
    });

    // the versions endpoint is a list, which getAction hands back keyed by index
    const versions: any[] = useMemo(() => Object.values(versionsData || {}), [versionsData]);

    const showDeclaration = (declaration: any, fallbackVersion?: any) => {
        setVersion(declaration?.version ?? fallbackVersion);
        setConfig(declaration?.declaration ?? {});
        setSchemaVersion(declaration?.schema?.version ?? 0);
    };

    const fetchVersion = (value: any) =>
        dataProvider.getAction(resource, { id: service_item.id, verb: 'declarations/versions/' + value.toString() })
            .then((response: any) => showDeclaration(response.data, value))
            .catch(() => { notify('Error fetching', 'error') });

    useEffect(() => {
        if (latestLoading) return;

        if (latestDeclarationData?.version != null) {
            showDeclaration(latestDeclarationData);
            return;
        }

        // `declarations/latest` only answers with a declaration whose change has
        // been approved or completed. An item whose first change is still pending
        // has none, and the picker used to sit on a version that does not exist
        // with nothing under it — so fall back to the newest version there is.
        const newest = versions.reduce(
            (latest: any, candidate: any) => (
                latest == null || candidate?.version > latest.version ? candidate : latest
            ),
            null,
        );
        if (newest?.version != null) fetchVersion(newest.version);
    }, [latestLoading, latestDeclarationData, versions])

    useEffect(() => {
        setOptions(
            versions.map((declaration: any) => ({
                value: declaration.version,
                label: <Space>Version {declaration.version} {getTagForStates(declaration.change_instance_state)}</Space>,
            }))
        )
    }, [versions])

    const handleVersionChange = (value: any) => { fetchVersion(value) }

    if (!versionsData) {
        return spinnerElement;
    }

    const isEmpty = !config || (typeof config === 'object' && Object.keys(config).length === 0);

    return (
        <div className="sid-decl">
            <div className="sid-toolbar">
                <div className="sid-toolbar__left">
                    {/* every option already reads "Version n" */}
                    <Select
                        value={version}
                        onChange={handleVersionChange}
                        options={options}
                        style={{ minWidth: 180 }}
                        placeholder="No versions"
                        aria-label="Select declaration version"
                    />
                </div>
                <span className="sid-chip-info">
                    {service_item.service?.name || "Service"} schema v{schemaVersion}
                    {service_item.is_validated_minimum_schema && (
                        <Tooltip title="This declaration has been validated against the minimum version schema" color="orange" placement="topRight">
                            <ExclamationCircleFilled style={{ color: 'orange' }} aria-label="Validated against the minimum version schema" />
                        </Tooltip>
                    )}
                </span>
            </div>

            {isEmpty ? (
                <div className="sid-empty">No declaration data for this version.</div>
            ) : (
                // the page keeps its own class on the frame, so its rules still apply
                <JsonPanel data={config} className="sid-json" enableYaml format="yaml" />
            )}
        </div>
    )
}
export default Declarations;
